import { Injectable, ViewContainerRef } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import moment from "moment";
import { InsServiceAbstract } from "../ins-service.abstract";
import { InsIdentityInfos } from "src/app/utils/interfaces/new/ins-identity-infos.interface";
import { LectureIns } from "src/app/utils/interfaces/new/lecture-ins.interface";
import { CheckInsRequestParam } from "src/app/utils/interfaces/new/check-ins-request-param";
import { IdentityToCheck } from "src/app/utils/interfaces/new/identity-to-check";
import { VitaleInfos } from "src/app/utils/interfaces/new/lecture-carte-vitale";
import {
  InsiRestApiError,
  InsiRestBaseBody,
  InsiRestCheckInsIdentityBody,
  InsiRestGetInsFromIdentityInformationBody,
  InsiRestGetInsFromRawVitaleDataBody,
  InsiRestLpsInfos,
  InsiRestResponse
} from "src/app/utils/interfaces/new/insi-rest-payloads.interface";
import { environment } from "src/environments/environment";
import { EdcAuthService } from "../../auth/edc-auth.service";
import { CpxHandleServiceAbstract, SessionTypeEnum, TypeConnecteurEnum } from "../../cpx-handle/cpx-handle-service-abstract";
import {
  ActionEnum,
  DomainEnum,
  LogFonctionnelCallerService,
  LogFonctionnelService,
  LogObject,
  ResultEnum,
  TypeTargetEnum
} from "../../log-fonctionnel/log-fonctionnel.service";
import { StringUtilsService } from "../../string-utils/string-utils.service";
import {
  CadreExerciceIsMissing,
  CannotCreateTlsiConnector,
  CannotRetreiveIns,
  MultipleIdentitiesFound,
  NoIdentityFound,
  NoIdentityInfos,
  NotSameVitaleCard
} from "../ins.error";
import {
  buildCheckInsRequestLogDetails,
  buildIdentityLogDetails,
  buildIdentityRequestLogParams,
  buildIdentitySearchLogDetails,
  buildInsiErrorLogDetails,
  buildInsiRequestLogDetails,
  CHECK_INS_LOG_SUBDOMAIN,
  INS_FROM_IDENTITY_LOG_SUBDOMAIN,
  INS_FROM_VITALE_LOG_SUBDOMAIN,
  INS_MULTIPLE_IDENTITIES_LOG_DETAILS,
  INS_NO_IDENTITY_LOG_DETAILS,
  INS_RETOUR_LOG_HEADER,
  INS_UNIQUE_IDENTITY_LOG_HEADER
} from "./ins-log.utils";
import { findRightHolderIndex, mapVitaleInfosToRawVitaleData } from "./ins-rest-mapper";

/** Même délai que le connecteur websocket (hl_createTlsiConnector) */
const INSI_TRANSACTION_TIMEOUT_IN_SECONDS = 60;
/** Code COG « lieu de naissance inconnu », refusé par l'INSi : le champ ne doit pas être envoyé (doc §8.2.4) */
const UNKNOWN_BIRTH_PLACE = '99999';
const PRACTICE_SETTING_MISSING_MESSAGE = "Le cadre d'exercice n'est pas renseigné. Vous devez le renseigner avant toutes choses.";

@Injectable({
  providedIn: 'root'
})
export class InsRestService extends InsServiceAbstract {
  public resourceId: string;

  /*
   * Cette URL pointe en réalité vers une Cloud Function
   * qui se charge de faire le lien avec le proxy iCanopée.
   * Cette Cloud Function est gérée dans le projet
   * back de Hypermed Online.
  **/
  private _edcProxyUrl: string;

  private _logCallSrv: LogFonctionnelCallerService;

  private LpsInfos: InsiRestLpsInfos = {
    s_idam: environment.iCanopeeIdam,
    s_numAM: environment.iCanopeeNumAM,
    s_version: environment.iCanopeeVersion,
    s_instance: environment.iCanopeeInstance,
    s_name: environment.iCanopeeName,
    s_billingNumber: this._formatBillingNumber(environment.iCanopeeBillingNumber)
  };

  constructor(
    private _http: HttpClient,
    private _edcAuthSrv: EdcAuthService,
    private _cpxHandlerSrv: CpxHandleServiceAbstract,
    private _logSrv: LogFonctionnelService,
    private _strUtilsSrv: StringUtilsService
  ) {
    super();
    if (!environment.edcProxyUrl) {
      throw new Error('EDC Proxy URL is not defined in environment configuration.');
    }
    this._edcProxyUrl = environment.edcProxyUrl;
  }

  private _formatBillingNumber(billingNumber: string): string | undefined {
    if (!billingNumber || billingNumber === '') {
      return undefined;
    } else if (billingNumber.length === 10) {
      return billingNumber.slice(1);
    } else if (billingNumber.length === 9) {
      return billingNumber;
    } else {
      return undefined;
    }
  }

  /**
   * Point d'entrée unique et générique du serveur proxy HTTP vers l'Espace de Confiance
   */
  private async _callProxy<T>(targetPath: string, payload: any): Promise<T> {
    if (payload && typeof payload === 'object') {
      const practiceSetting = this._edcAuthSrv.practice_setting;
      if (!practiceSetting) {
        throw new Error(PRACTICE_SETTING_MISSING_MESSAGE);
      }
      payload.PracticeLocation = {
        s_practiceSettings: practiceSetting
      };
      payload.s_correlationId = this._edcAuthSrv.s_correlationId || '';
    }

    const userName = this._edcAuthSrv.user_name;
    const userGiven = this._edcAuthSrv.user_given;
    const idNatPs = this._edcAuthSrv.id_nat_ps;

    if (!userName || !userGiven || !idNatPs) {
      throw new Error("Les informations d'authentification du médecin sont incomplètes. Veuillez vous reconnecter.");
    }

    const headers = new HttpHeaders()
      .set('X-Target-Path', targetPath)
      .set('X-User-Name', userName)
      .set('X-User-Given', userGiven)
      .set('X-IdNatPs', idNatPs);
    return this._http.post<T>(this._edcProxyUrl, payload, { headers }).toPromise();
  }

  /**
   * Exécute une transaction INSi.
   * Les erreurs du téléservice (HTTP en erreur ou s_status différent de 'OK') sont retournées dans apiError,
   * sous la forme à plat renvoyée par le proxy, équivalente au response.error du connecteur websocket.
   * Les autres erreurs (paramétrage, identité du PS incomplète) sont relayées telles quelles.
   */
  private async _callInsiTransaction<T extends InsiRestResponse>(
    targetPath: string,
    payload: InsiRestBaseBody
  ): Promise<{ response?: T, apiError?: InsiRestApiError }> {
    let response: T;
    try {
      response = await this._callProxy<T>(targetPath, payload);
    } catch (e) {
      if (e instanceof HttpErrorResponse) {
        return { apiError: this._toApiError(e) };
      }
      throw e;
    }

    if (response && response.s_status && response.s_status !== 'OK') {
      return { apiError: response as InsiRestApiError };
    }
    return { response };
  }

  private _toApiError(e: HttpErrorResponse): InsiRestApiError {
    if (e.error && typeof e.error === 'object') {
      return e.error;
    }
    return {
      s_status: 'ERROR',
      s_apiErrorDescription: e.message,
      s_apiErrorExtendedInformations: typeof e.error === 'string' ? e.error : undefined
    };
  }

  /**
   * Champs communs à toutes les transactions INSi (doc §8.1.1 et §8.1.3)
   */
  private _getBasePayload(): InsiRestBaseBody {
    return {
      LpsInfos: this.LpsInfos,
      s_insiUrl: (environment.tlsiServerEsUrl || '').trim(),
      i_timeout: INSI_TRANSACTION_TIMEOUT_IN_SECONDS
    };
  }

  /**
   * Équivalent REST de createTlsiConnector (stratégie websocket) : s'assure qu'une session Espace de Confiance est ouverte.
   * En mode REST, getCpxSession déclenche la connexion PSC (connectUser) si aucun s_correlationId n'est disponible.
   *
   * @returns false si aucune session n'a pu être obtenue
   */
  private async _ensureEdcSession(view: ViewContainerRef): Promise<boolean> {
    let cpxSession: any;
    try {
      cpxSession = await this._cpxHandlerSrv.getCpxSession(view, SessionTypeEnum.CARTE, TypeConnecteurEnum.DMPConnect);
    } catch (e) {
      console.error(e);
      throw new CannotCreateTlsiConnector(e?.message);
    }

    if (!cpxSession) {
      return false;
    }

    if (!this._edcAuthSrv.practice_setting) {
      throw new CadreExerciceIsMissing(PRACTICE_SETTING_MISSING_MESSAGE);
    }

    return true;
  }

  /** Format imposé par l'INSi : majuscules, sans signe diacritique (doc §8.1.2). Champ obligatoire : jamais undefined */
  private _normalizeName(value: string): string {
    return value ? this._strUtilsSrv.uppercaseWithoutAccents(value) : '';
  }

  /** Le lieu de naissance n'est pas envoyé s'il est inconnu (doc §8.2.4) */
  private _normalizeBirthPlace(birthPlace: string): string | undefined {
    return birthPlace && birthPlace !== UNKNOWN_BIRTH_PLACE ? birthPlace : undefined;
  }

  /** Format imposé par l'INSi : AAAA-MM-JJ (doc §8.1.2). Champ obligatoire : jamais undefined */
  private _normalizeBirthDate(birthDate: string): string {
    return birthDate ? moment(birthDate).format('YYYY-MM-DD') : '';
  }

  /**
   * Données d'un bénéficiaire et numéro de série de la carte Vitale, fournis par le connecteur websocket
   * (hl_getVitaleInfos) : la lecture de la carte reste locale. Retourne undefined si elles n'ont pas pu être obtenues.
   */
  private async _getVitaleInfos(vitaleIndex: number): Promise<VitaleInfos | undefined> {
    try {
      return await this._cpxHandlerSrv.getVitaleInfos(vitaleIndex);
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  public async getInsFromIdentityInformation(view: ViewContainerRef, identityInfos: InsIdentityInfos): Promise<LectureIns> {
    if (!await this._ensureEdcSession(view)) {
      return undefined;
    }

    if (!identityInfos) {
      throw new NoIdentityInfos();
    }

    const payload: InsiRestGetInsFromIdentityInformationBody = {
      ...this._getBasePayload(),
      s_birthName: this._normalizeName(identityInfos.s_birthName),
      s_given: this._normalizeName(identityInfos.s_given),
      i_sex: identityInfos.i_sex,
      s_birthDate: this._normalizeBirthDate(identityInfos.s_birthDate),
      s_birthPlace: this._normalizeBirthPlace(identityInfos.s_birthPlace)
    };

    const searchInfo = buildIdentitySearchLogDetails(payload);
    const requeteInfo = buildInsiRequestLogDetails('/getInsFromIdentityInformation', payload.LpsInfos, buildIdentityRequestLogParams(payload));

    const { response, apiError } = await this._callInsiTransaction<LectureIns>('/getInsFromIdentityInformation', payload);
    if (apiError) {
      this._addReadLog(ResultEnum.Error, INS_FROM_IDENTITY_LOG_SUBDOMAIN, searchInfo + INS_RETOUR_LOG_HEADER + buildInsiErrorLogDetails(apiError) + requeteInfo);
      throw new CannotRetreiveIns(apiError.s_apiErrorExtendedInformations);
    }

    if (response.i_insIdentityResult === 1) {
      this._addReadLog(ResultEnum.Error, INS_FROM_IDENTITY_LOG_SUBDOMAIN, searchInfo + INS_NO_IDENTITY_LOG_DETAILS + requeteInfo);
      throw new NoIdentityFound();
    } else if (response.i_insIdentityResult === 2) {
      const details = searchInfo + buildIdentityLogDetails(response, INS_UNIQUE_IDENTITY_LOG_HEADER) + requeteInfo;
      this._addReadLog(ResultEnum.Success, INS_FROM_IDENTITY_LOG_SUBDOMAIN, details);
      return this.isLectureInsOk(response) ? response : undefined;
    } else if (response.i_insIdentityResult === 3) {
      this._addReadLog(ResultEnum.Success, INS_FROM_IDENTITY_LOG_SUBDOMAIN, searchInfo + INS_MULTIPLE_IDENTITIES_LOG_DETAILS + requeteInfo);
      throw new MultipleIdentitiesFound();
    }
    return undefined;
  }

  /**
   * La lecture de la carte reste réalisée par le connecteur websocket (CpxHandleServiceAbstract.lectureVitale) :
   * on réutilise ici la dernière lecture, comme le fait le connecteur avec hl_getInsFromVitaleCard,
   * et seul l'appel au téléservice INSi passe par le proxy REST.
   */
  public async getInsFromVitaleCard(view: ViewContainerRef, vitaleIndex: number): Promise<LectureIns> {
    if (!await this._ensureEdcSession(view)) {
      return undefined;
    }

    const vitaleRead = this._cpxHandlerSrv.getLastVitaleRead();
    if (!vitaleRead) {
      throw new Error('Aucune carte Vitale n\'a été lue. Veuillez lire la carte Vitale du patient.');
    }

    if (!await this._cpxHandlerSrv.isSameVitaleCard()) {
      throw new NotSameVitaleCard('La carte Vitale a changé depuis la dernière lecture. Veuillez relire la carte Vitale.');
    }

    if (!vitaleRead.Patients?.[vitaleIndex]) {
      throw new Error('Le bénéficiaire sélectionné est introuvable sur la carte Vitale.');
    }

    const beneficiaryInfos = await this._getVitaleInfos(vitaleIndex);
    if (!beneficiaryInfos?.VitaleData?.s_serialNumber) {
      throw new Error('Le numéro de série de la carte Vitale n\'a pas pu être lu. Veuillez relire la carte Vitale.');
    }

    const rightHolderIndex = findRightHolderIndex(vitaleRead);
    let rightHolderInfos: VitaleInfos;
    if (rightHolderIndex === vitaleIndex) {
      rightHolderInfos = beneficiaryInfos;
    } else if (rightHolderIndex !== -1) {
      rightHolderInfos = await this._getVitaleInfos(rightHolderIndex);
    }

    const rawVitaleData = mapVitaleInfosToRawVitaleData(beneficiaryInfos, rightHolderInfos);
    if (!rawVitaleData) {
      throw new Error('Les données de la carte Vitale sont incomplètes. Veuillez relire la carte Vitale.');
    }

    const payload: InsiRestGetInsFromRawVitaleDataBody = {
      ...this._getBasePayload(),
      ...rawVitaleData
    };

    const requeteInfo = buildInsiRequestLogDetails('/getInsFromRawVitaleData', payload.LpsInfos, [`VitaleIndex : ${vitaleIndex}`]);

    const { response, apiError } = await this._callInsiTransaction<LectureIns>('/getInsFromRawVitaleData', payload);
    if (apiError) {
      this._addReadLog(ResultEnum.Error, INS_FROM_VITALE_LOG_SUBDOMAIN, INS_RETOUR_LOG_HEADER + buildInsiErrorLogDetails(apiError) + requeteInfo);
      throw apiError;
    }

    if (response.i_insIdentityResult === 1) {
      this._addReadLog(ResultEnum.Error, INS_FROM_VITALE_LOG_SUBDOMAIN, INS_NO_IDENTITY_LOG_DETAILS + requeteInfo);
      throw new NoIdentityFound();
    } else if (response.i_insIdentityResult === 3) {
      this._addReadLog(ResultEnum.Success, INS_FROM_VITALE_LOG_SUBDOMAIN, INS_MULTIPLE_IDENTITIES_LOG_DETAILS + requeteInfo);
      throw new MultipleIdentitiesFound();
    }

    this._addReadLog(ResultEnum.Success, INS_FROM_VITALE_LOG_SUBDOMAIN, buildIdentityLogDetails(response, INS_UNIQUE_IDENTITY_LOG_HEADER) + requeteInfo);
    return response;
  }

  /**
   * Vérifie l'identité d'un patient à partir de ses informations
   *
   * @returns true si l'identité est vérifiée, false si l'INSi la rejette
   */
  public async checkInsIdentity(view: ViewContainerRef, param: CheckInsRequestParam): Promise<boolean> {
    param.s_birthDate = moment(param.s_birthDate).format('YYYY-MM-DD');
    const objetLog: LogObject = {
      action: ActionEnum.Read,
      result: ResultEnum.Unknown,
      typeTarget: TypeTargetEnum.NONE,
      subDomain: CHECK_INS_LOG_SUBDOMAIN,
      details: buildCheckInsRequestLogDetails(param, (str) => this._normalizeName(str))
    };

    try {
      if (!await this._ensureEdcSession(view)) {
        return undefined;
      }
    } catch (e) {
      objetLog.details += 'Erreur lors de la création du connecteur Tlsi';
      objetLog.details += '\n' + e.message;
      objetLog.result = ResultEnum.Error;
      this._addLog(objetLog);
      throw e;
    }

    const payload: InsiRestCheckInsIdentityBody = {
      ...this._getBasePayload(),
      s_ins: param.s_ins || '',
      s_key: param.s_key || '',
      s_oid: param.s_oid || '',
      s_birthName: this._normalizeName(param.s_birthName),
      s_given: this._normalizeName(param.s_given),
      i_sex: param.i_sex,
      s_birthDate: param.s_birthDate,
      s_birthPlace: this._normalizeBirthPlace(param.s_birthPlace)
    };

    let apiError: InsiRestApiError;
    try {
      apiError = (await this._callInsiTransaction<InsiRestResponse>('/checkInsIdentity', payload)).apiError;
    } catch (e) {
      objetLog.result = ResultEnum.Error;
      this._addLog(objetLog);
      throw e;
    }

    if (!apiError) {
      objetLog.result = ResultEnum.Success;
      this._addLog(objetLog);
      return true;
    }

    objetLog.result = ResultEnum.Error;
    objetLog.details += buildInsiErrorLogDetails(apiError, true);
    this._addLog(objetLog);

    if (this._isIdentityRejected(apiError)) {
      return false;
    }
    throw apiError;
  }

  /** Erreurs signifiant que l'identité n'est pas vérifiée, et non une erreur technique (cf. stratégie websocket) */
  private _isIdentityRejected(apiError: InsiRestApiError): boolean {
    return (apiError.i_apiErrorCode === 3 && apiError.i_apiErrorType === 1)
      || (apiError.i_apiErrorCode === 16384 && apiError.i_apiErrorType === 6);
  }

  public async massCheckInsIdentity(view: ViewContainerRef, param: Array<IdentityToCheck>): Promise<any> {
    throw new Error('La vérification INSi en masse n\'est pas disponible avec l\'API REST.');
  }

  private _addReadLog(result: ResultEnum, subDomain: string, details: string): void {
    this._addLog({ action: ActionEnum.Read, result, typeTarget: TypeTargetEnum.NONE, subDomain, details });
  }

  private async _addLog(obj: LogObject): Promise<void> {
    try {
      if (!this._logCallSrv) {
        this._logCallSrv = this._logSrv.getConfiguration(DomainEnum.Insi);
      }

      this._logCallSrv.addLog(obj);
    } catch (e) {
      console.error(e);
    }
  }
}
